/**
 * Badge Component
 * 
 * A versatile badge component for displaying status, counts, or labels.
 * Supports multiple variants and sizes.
 */
import * as React from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '@/lib/utils'

interface BadgeProps {
  /**
   * The content to display inside the badge
   */
  children: React.ReactNode
  
  /**
   * The visual style variant of the badge
   */
  variant?: 'default' | 'primary' | 'secondary' | 'accent' | 'destructive' | 'success' | 'warning' | 'info' | 'outline'
  
  /**
   * The size of the badge
   */
  size?: 'sm' | 'md' | 'lg'
  
  /**
   * Additional CSS classes
   */
  className?: string
  
  /**
   * Whether the badge is rounded (pill shape)
   */
  rounded?: boolean
  
  /**
   * Optional icon to display before the content
   */
  icon?: React.ReactNode
  
  /**
   * Optional click handler
   */
  onClick?: () => void
}

const badgeVariants = cva(
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/80",
        primary:
          "bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        accent:
          "bg-accent text-accent-foreground hover:bg-accent/80",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/80",
        success:
          "bg-green-500 text-white hover:bg-green-600",
        warning:
          "bg-yellow-500 text-white hover:bg-yellow-600",
        info:
          "bg-blue-500 text-white hover:bg-blue-600",
        outline:
          "text-foreground border border-input hover:bg-accent hover:text-accent-foreground",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

/**
 * Badge component for displaying status, counts, or labels
 * 
 * @example
 * // Basic usage
 * <Badge>New</Badge>
 * 
 * @example
 * // With variant and size
 * <Badge variant="success" size="sm">Completed</Badge>
 * 
 * @example
 * // With icon
 * <Badge variant="warning" icon={<AlertIcon />}>Warning</Badge>
 */
function Badge({
  children,
  variant = 'default',
  size = 'md',
  className,
  rounded = false,
  icon,
  onClick,
}: BadgeProps) {
  // Base styles
  const baseStyles = 'inline-flex items-center justify-center font-medium'
  
  // Variant styles
  const variantStyles = badgeVariants({ variant })
  
  // Size styles
  const sizeStyles = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-0.5',
    lg: 'text-base px-3 py-1',
  }
  
  // Border radius based on rounded prop
  const radiusStyle = rounded ? 'rounded-full' : 'rounded-md'
  
  // Clickable styles
  const clickableStyles = onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''
  
  return (
    <span
      className={cn(
        baseStyles,
        variantStyles,
        sizeStyles[size],
        radiusStyle,
        clickableStyles,
        className
      )}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {icon && <span className="mr-1">{icon}</span>}
      {children}
    </span>
  )
}

export default Badge
